import React, { useState, useRef, useEffect } from 'react';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  Avatar,
  Chip,
  Button,
  Tooltip,
  Zoom,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
  Slider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { GlassSpinner } from '../common/GlassLoader';
import { styled, keyframes } from '@mui/material/styles';
import {
  Psychology as AiraIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  Chat,
  Book,
  Air,
  CheckCircle,
  Error,
  Mood,
  SentimentVeryDissatisfied,
  SentimentDissatisfied,
  SentimentNeutral,
  SentimentSatisfied,
  SentimentVerySatisfied,
  HealthAndSafety,
  FitnessCenter,
  Lightbulb,
  ArrowBack,
  NavigateNext,
  PlayArrow,
  Timer,
  Tune as TuneIcon,
  Description as PolicyIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { aiAPI, AIMesaira } from '../../services/api/aiAPI';
import wellnessAPI from '../../services/api/wellnessAPI';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import WellnessCheckInModal from './WellnessCheckInModal';
import toast from 'react-hot-toast';
import { useBranding } from '../../contexts/ThemeContext';
import { useUserBot } from '../../hooks/useUserBot';
import UserBotCustomizer from './UserBotCustomizer';

const SUGGESTED_QUESTIONS = [
  'What is our leave policy?',
  'How do I request time off?',
  'What are the working hours?',
  'What is the code of conduct?',
  'How do I report a concern?',
  'What benefits am I entitled to?',
  'What is the remote work policy?',
  'How does performance review work?',
];

// Theme constants
const LIME_GREEN_DARK = '#244BC0';
const AIRA_GRADIENT = 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)';

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

// Styled Components
interface AiraFabProps {
  hasPageFab?: boolean;
}

const AiraFab = styled(Fab, {
  shouldForwardProp: (prop) => prop !== 'hasPageFab',
})<AiraFabProps>(({ theme, hasPageFab }) => ({
  position: 'fixed',
  bottom: hasPageFab ? theme.spacing(14) : theme.spacing(12),
  right: theme.spacing(3),
  // The launcher used to be a blue block with a hardcoded gradient, which was
  // the one thing on screen that ignored the tenant's colours. It is now the
  // page's own surface inside a brand-coloured ring, so the assistant reads as
  // part of the company's app rather than a third-party widget bolted on.
  background: theme.palette.background.paper,
  color: theme.palette.primary.main,
  border: `2px solid ${theme.palette.primary.main}`,
  width: 72,
  height: 72,
  borderRadius: '24px',
  boxShadow: '0 8px 24px rgba(17, 24, 39, 0.14)',
  animation: `${float} 4s ease-in-out infinite`,
  '&:hover': {
    background: theme.palette.background.paper,
    borderColor: theme.palette.primary.dark,
    transform: 'scale(1.05)',
    animation: 'none',
  },
  // Above page content, below the app bars and every dialog. It sat above
  // tooltips (1502) and so floated over the buttons of every open dialog.
  zIndex: theme.zIndex.speedDial,
  [theme.breakpoints.down('md')]: {
    width: 56,
    height: 56,
    borderRadius: '18px',
    bottom: `calc(var(--app-bottom-inset) + ${hasPageFab ? 88 : 16}px)`,
    right: 'calc(16px + env(safe-area-inset-right))',
  },
}));

// Screens where the floating button sits on top of what the person is doing
// on a phone — game boards, quiz answers, players, the readers and the chat
// composers. The assistant stays a tap away everywhere else.
const PHONE_IMMERSIVE_PATHS = [
  '/games/', '/live-quiz', '/gamification/quiz', '/chat', '/ask-ai',
  '/library/', '/learning/library/', '/learning/journeys/module/', '/learning/course/',
];

const MesairaBubble = styled(Paper)(({ theme, isUser }: { theme?: any; isUser: boolean }) => ({
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(1.5),
  maxWidth: '85%',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  backgroundColor: isUser ? '#3B82F6' : 'var(--surface)',
  color: isUser ? 'white' : '#1A1D1F',
  borderRadius: '20px',
  boxShadow: isUser 
    ? '0 4px 12px rgba(59, 130, 246, 0.3)' 
    : '0 2px 8px rgba(0, 0, 0, 0.06)',
  ...(isUser && {
    borderBottomRightRadius: '6px',
    background: AIRA_GRADIENT,
  }),
  ...(!isUser && {
    borderBottomLeftRadius: '6px',
    border: '1px solid var(--hairline)',
  }),
}));

interface MoodEntry {
  id: string;
  date: string;
  mood: number;
  energy: number;
  stress: number;
  notes?: string;
  activities: string[];
}

interface WellnessGoal {
  id: string;
  title: string;
  description: string;
  target: number;
  current: number;
  unit: string;
  category: 'physical' | 'mental' | 'social' | 'spiritual';
  deadline: string;
}

interface Recommendation {
  id: string;
  type: 'exercise' | 'meditation' | 'social' | 'learning' | 'nutrition' | 'sleep';
  title: string;
  description: string;
  duration: string;
  difficulty: 'easy' | 'medium' | 'hard';
  benefits: string[];
  icon: React.ReactNode;
}

function EnhancedAiraAI() {
  const { branding } = useBranding();
  const LIME_GREEN = branding.primaryColor;
  const { user, needsQuestionnaire } = useAuth();
  const location = useLocation();
  // Per-user bot identity. Falls back to company default -> "Aira".
  const userBot = useUserBot();
  const botName = userBot.botName;
  const botPictureUrl = userBot.botPicture
    ? resolveAssetUrl(userBot.botPicture)
    : (branding.airaIconUrl ? resolveAssetUrl(branding.airaIconUrl) : null);
  const [showBotCustomizer, setShowBotCustomizer] = useState(false);
  
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Check if current page has its own FAB button that might overlap
  const hasPageFab = location.pathname.startsWith('/app/projects') ||
                     location.pathname === '/projects' ||
                     location.pathname === '/pulse';
  const hideFab = isMobile && PHONE_IMMERSIVE_PATHS.some((p) => location.pathname.startsWith(p));
  
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [mesairas, setMesairas] = useState<AIMesaira[]>([]);
  const [inputMesaira, setInputMesaira] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [, setIsSpeaking] = useState(false);
  const [voiceEnabled, ] = useState(false);
  const mesairasEndRef = useRef<HTMLDivElement>(null);
  const conversationIdRef = useRef<string>('');

  const [showWellnessModal, setShowWellnessModal] = useState(false);
  
  // Check if wellness was already shown/skipped today using localStorage
  const getWellnessSkippedToday = () => {
    const skippedDate = localStorage.getItem('wellnessSkippedDate');
    const today = new Date().toISOString().split('T')[0];
    return skippedDate === today;
  };
  
  const setWellnessSkippedToday = () => {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem('wellnessSkippedDate', today);
  };
  
  const [wellnessChecked, setWellnessChecked] = useState(getWellnessSkippedToday());

  // Mental Health State
  const [currentMood, setCurrentMood] = useState(3);
  const [currentEnergy, setCurrentEnergy] = useState(3);
  const [currentStress, setCurrentStress] = useState(3);
  const [moodNotes, setMoodNotes] = useState('');
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);

  // Real data from API - initialized empty
  const [moodHistory, setMoodHistory] = useState<MoodEntry[]>([]);
  const [wellnessGoals, setWellnessGoals] = useState<WellnessGoal[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [, setWellnessLoading] = useState(true);

  const [activeExercise, setActiveExercise] = useState<{title: string; type: string; steps: {instruction: string; duration: string; tip?: string}[]} | null>(null);
  const [exerciseStep, setExerciseStep] = useState(0);
  const [exerciseLoading, setExerciseLoading] = useState(false);
  const [exerciseTimer, setExerciseTimer] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const activities = [
    'work', 'exercise', 'socializing', 'learning', 'meditation', 'hobbies',
    'family time', 'rest', 'outdoor activities', 'creative work'
  ];

  useEffect(() => {
    if (mesairas.length > 0) {
      scrollToBottom();
    }
  }, [mesairas]);

  useEffect(() => {
    // Check today's status for the FAB "!" badge only. The modal no longer
    // auto-opens over every page — the dashboard's Today strip and this badge
    // are the nudges; the modal opens on explicit user intent.
    const checkWellnessStatus = async () => {
      try {
        const status = await wellnessAPI.checkStatus();
        if (status.todayCompleted) {
          setWellnessChecked(true);
          setWellnessSkippedToday();
        }
      } catch (error) {
        console.error('Error checking wellness status:', error);
      }
    };

    if (user && !wellnessChecked && user.role !== 'super-super-admin' && !user.mustChangePassword && !needsQuestionnaire) {
      checkWellnessStatus();
    }
  }, [user, wellnessChecked, needsQuestionnaire]);

  // Fetch wellness data from API
  useEffect(() => {
    const loadWellnessData = async () => {
      if (!user) return;
      
      setWellnessLoading(true);
      try {
        // Fetch mood history from wellness check-ins
        const historyResponse = await wellnessAPI.getCheckInHistory();
        if (historyResponse && Array.isArray(historyResponse)) {
          const formattedHistory: MoodEntry[] = historyResponse.slice(0, 7).map((checkIn: any) => ({
            id: checkIn.id?.toString() || Date.now().toString(),
            date: checkIn.checkInDate || checkIn.date || checkIn.createdAt?.split('T')[0] || new Date().toISOString().split('T')[0],
            mood: checkIn.moodScore || checkIn.mood || 3,
            energy: checkIn.energy || 3,
            stress: checkIn.stress || 3,
            notes: checkIn.notes || checkIn.responses || '',
            activities: checkIn.activities || []
          }));
          setMoodHistory(formattedHistory);
        }

        // Generate personalized recommendations based on mood history
        const avgMood = moodHistory.length > 0 
          ? moodHistory.reduce((sum, m) => sum + m.mood, 0) / moodHistory.length 
          : 3;
        
        const personalizedRecs: Recommendation[] = [];
        
        if (avgMood < 3) {
          personalizedRecs.push({
            id: '1',
            type: 'meditation',
            title: '5-Minute Breathing Exercise',
            description: 'Quick stress relief through focused breathing',
            duration: '5 minutes',
            difficulty: 'easy',
            benefits: ['Reduces stress', 'Improves focus', 'Calms mind'],
            icon: <Air />
          });
          personalizedRecs.push({
            id: '2',
            type: 'social',
            title: 'Reach Out to Someone',
            description: 'Connect with a colleague or friend',
            duration: '10 minutes',
            difficulty: 'easy',
            benefits: ['Builds support', 'Reduces isolation', 'Improves mood'],
            icon: <Chat />
          });
        } else {
          personalizedRecs.push({
            id: '1',
            type: 'exercise',
            title: 'Desk Stretches',
            description: 'Simple stretches to maintain energy',
            duration: '3 minutes',
            difficulty: 'easy',
            benefits: ['Reduces tension', 'Improves posture', 'Boosts energy'],
            icon: <FitnessCenter />
          });
          personalizedRecs.push({
            id: '2',
            type: 'learning',
            title: 'Mindfulness Reading',
            description: 'Learn new wellness techniques',
            duration: '10 minutes',
            difficulty: 'medium',
            benefits: ['Increases knowledge', 'Provides tools', 'Inspires growth'],
            icon: <Book />
          });
        }
        
        setRecommendations(personalizedRecs);

        // Set default wellness goals based on user activity
        const defaultGoals: WellnessGoal[] = [
          {
            id: '1',
            title: 'Daily Check-ins',
            description: 'Complete your daily wellness check-in',
            target: 7,
            current: moodHistory.length,
            unit: 'days',
            category: 'mental',
            deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          },
          {
            id: '2',
            title: 'Mindful Moments',
            description: 'Take short breaks for mindfulness',
            target: 5,
            current: Math.min(moodHistory.length, 5),
            unit: 'sessions',
            category: 'mental',
            deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          }
        ];
        setWellnessGoals(defaultGoals);

      } catch (error) {
        console.error('Error loading wellness data:', error);
        // Set empty defaults on error
        setMoodHistory([]);
        setWellnessGoals([]);
        setRecommendations([]);
      } finally {
        setWellnessLoading(false);
      }
    };

    loadWellnessData();
  }, [user]);

  useEffect(() => {
    // Initialize with welcome mesaira
    if (mesairas.length === 0) {
      const welcomeMesaira: AIMesaira = {
        id: '1',
        content: `Hello ${user?.firstName || user?.email?.split('@')[0] || 'there'}! I'm ${botName}, your AI assistant. I know all about your company's policies, procedures, benefits, and announcements.\n\nAsk me anything — company policies, wellness, work advice, or whatever's on your mind!`,
        role: 'assistant',
        timestamp: new Date().toISOString()
      };
      setMesairas([welcomeMesaira]);
    }
  }, [user, mesairas.length]);

  const scrollToBottom = () => {
    mesairasEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const callBackendAPI = async (mesaira: string): Promise<{ response: string; citations?: Array<{ id: number; title: string }> }> => {
    const history = mesairas
      .filter(m => m.id !== '1')
      .slice(-8)
      .map(m => ({ role: m.role, content: m.content }));

    const res = await aiAPI.askCompanyQuestion({
      question: mesaira,
      conversationHistory: history,
      conversationId: conversationIdRef.current || undefined,
    });

    if (res.success && res.data) {
      if (res.data.conversationId) conversationIdRef.current = res.data.conversationId;
      return { response: res.data.response, citations: res.data.citations };
    }
    throw new Error('AI service unavailable');
  };

  const analyzeSentiment = (text: string): 'positive' | 'negative' | 'neutral' => {
    const lowerText = text.toLowerCase();
    
    const positiveWords = ['happy', 'good', 'great', 'excellent', 'wonderful', 'amazing', 'fantastic', 'love', 'excited', 'grateful', 'thankful', 'blessed', 'joy', 'smile'];
    const negativeWords = ['sad', 'bad', 'terrible', 'awful', 'hate', 'angry', 'frustrated', 'depressed', 'anxious', 'worried', 'stressed', 'overwhelmed', 'tired', 'exhausted'];
    
    const positiveCount = positiveWords.filter(word => lowerText.includes(word)).length;
    const negativeCount = negativeWords.filter(word => lowerText.includes(word)).length;
    
    if (positiveCount > negativeCount) return 'positive';
    if (negativeCount > positiveCount) return 'negative';
    return 'neutral';
  };

  const handleSendSuggested = (question: string) => {
    setInputMesaira(question);
    // slight delay so state updates before send
    setTimeout(() => {
      setInputMesaira('');
      const userMesaira: AIMesaira = {
        id: Date.now().toString(),
        content: question,
        role: 'user',
        timestamp: new Date().toISOString(),
      };
      setMesairas(prev => [...prev, userMesaira]);
      setIsLoading(true);
      callBackendAPI(question).then(({ response: aiResponse, citations }) => {
        setMesairas(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          content: aiResponse,
          role: 'assistant',
          timestamp: new Date().toISOString(),
          citations: citations?.length ? citations : undefined,
        }]);
        setIsLoading(false);
      }).catch(() => {
        setMesairas(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          content: "I'm having trouble connecting right now. Please try again.",
          role: 'assistant',
          timestamp: new Date().toISOString(),
        }]);
        setIsLoading(false);
      });
    }, 0);
  };

  const handleSendMesaira = async () => {
    if (!inputMesaira.trim()) return;

    const userMesaira: AIMesaira = {
      id: Date.now().toString(),
      content: inputMesaira,
      role: 'user',
      timestamp: new Date().toISOString()
    };

    setMesairas(prev => [...prev, userMesaira]);
    setInputMesaira('');
    setIsLoading(true);

    try {
      const { response: aiResponse, citations } = await callBackendAPI(inputMesaira);

      const aiMesaira: AIMesaira = {
        id: (Date.now() + 1).toString(),
        content: aiResponse,
        role: 'assistant',
        timestamp: new Date().toISOString(),
        citations: citations?.length ? citations : undefined,
        metadata: {
          sentiment: analyzeSentiment(inputMesaira)
        }
      };

      setMesairas(prev => [...prev, aiMesaira]);
      setIsLoading(false);

      if (voiceEnabled) {
        speakMesaira(aiResponse);
      }
    } catch (error) {
      console.error('Error sending message to AI:', error);
      setIsLoading(false);

      const aiMesaira: AIMesaira = {
        id: (Date.now() + 1).toString(),
        content: "I'm having trouble connecting right now. Please try again shortly or contact HR directly for policy questions.",
        role: 'assistant',
        timestamp: new Date().toISOString()
      };
      setMesairas(prev => [...prev, aiMesaira]);
      toast.error('Connection issue — please try again.');
    }
  };

  const generateAIResponse = (input: string): string => {
    const lowerInput = input.toLowerCase();
    
    if (lowerInput.includes('stress') || lowerInput.includes('anxious')) {
      return "I understand you're feeling stressed. Let's try a quick breathing exercise: Breathe in for 4 counts, hold for 4, then breathe out for 6. This can help activate your parasympathetic nervous system and reduce stress. Would you like me to guide you through a longer relaxation exercise?";
    }
    
    if (lowerInput.includes('tired') || lowerInput.includes('energy')) {
      return "Low energy can be challenging. Consider taking a 5-minute walk, doing some light stretches, or having a healthy snack. Sometimes a brief change of environment can boost energy levels. How has your sleep been lately?";
    }
    
    if (lowerInput.includes('mood') || lowerInput.includes('feeling')) {
      return "Thank you for sharing how you're feeling. Tracking your mood regularly can help identify patterns and triggers. I notice you haven't logged your mood today - would you like to do a quick check-in? It only takes a minute and can provide valuable insights.";
    }
    
    if (lowerInput.includes('work') || lowerInput.includes('project')) {
      return "Work can be both rewarding and challenging. Remember to take regular breaks and celebrate small wins. If you're feeling overwhelmed, try breaking tasks into smaller, manageable pieces. What aspect of work is on your mind today?";
    }
    
    return "I'm here to listen and support you. Whether you need to talk through something, want wellness tips, or just need a moment to pause, I'm here. What would be most helpful for you right now?";
  };

  const speakMesaira = (text: string) => {
    if ('speechSynthesis' in window) {
      setIsSpeaking(true);
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1.1;
      utterance.onend = () => setIsSpeaking(false);
      speechSynthesis.speak(utterance);
    }
  };

  const handleMoodSubmit = () => {
    const newEntry: MoodEntry = {
      id: Date.now().toString(),
      date: new Date().toISOString().split('T')[0],
      mood: currentMood,
      energy: currentEnergy,
      stress: currentStress,
      notes: moodNotes,
      activities: selectedActivities
    };

    setMoodHistory(prev => [newEntry, ...prev]);
    toast.success('Mood logged successfully!');
    
    // Reset form
    setCurrentMood(3);
    setCurrentEnergy(3);
    setCurrentStress(3);
    setMoodNotes('');
    setSelectedActivities([]);
  };

  const handleWellnessComplete = (suggestions: string[]) => {
    setWellnessChecked(true);
    setWellnessSkippedToday(); // Mark as completed for today so it doesn't show again
    setShowWellnessModal(false);
    
    // Show a toast with the first suggestion instead of opening Ask Aira
    if (suggestions && suggestions.length > 0) {
      toast.success(`Wellness tip: ${suggestions[0]}`);
    } else {
      toast.success('Wellness check-in completed!');
    }
  };

  const getMoodIcon = (mood: number) => {
    switch (mood) {
      case 1: return <SentimentVeryDissatisfied color="error" />;
      case 2: return <SentimentDissatisfied color="warning" />;
      case 3: return <SentimentNeutral color="action" />;
      case 4: return <SentimentSatisfied color="primary" />;
      case 5: return <SentimentVerySatisfied color="success" />;
      default: return <SentimentNeutral />;
    }
  };

  // const _getMoodColor = (mood: number) => {
  //   switch (mood) {
  //     case 1: return 'error';
  //     case 2: return 'warning';
  //     case 3: return 'info';
  //     case 4: return 'primary';
  //     case 5: return 'success';
  //     default: return 'info';
  //   }
  // };

  const getGoalProgress = (goal: WellnessGoal) => {
    return Math.min((goal.current / goal.target) * 100, 100);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMesaira();
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleStartActivity = async (rec: Recommendation) => {
    setExerciseLoading(true);
    setActiveExercise(null);
    setExerciseStep(0);
    setTimerActive(false);
    if (timerRef.current) clearInterval(timerRef.current);
    try {
      const prompt = `Generate a guided ${rec.type} exercise called "${rec.title}" (${rec.duration}, ${rec.difficulty} difficulty). Return ONLY a JSON object with this exact structure: {"steps": [{"instruction": "step text", "duration": "time", "tip": "optional tip"}]}. Include 5-7 interactive steps. Make instructions clear, actionable, and encouraging. Focus on ${rec.type} wellness.`;
      const { response } = await callBackendAPI(prompt);
      let steps;
      try {
        const jsonMatch = response.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          steps = parsed.steps;
        }
      } catch (e) {
        steps = null;
      }
      if (!steps || !Array.isArray(steps) || steps.length === 0) {
        const fallbackSteps: Record<string, {instruction: string; duration: string; tip?: string}[]> = {
          exercise: [
            { instruction: 'Stand up and shake out your arms and legs to loosen up.', duration: '30s', tip: 'Roll your shoulders back a few times' },
            { instruction: 'Do 10 gentle arm circles — 5 forward, 5 backward.', duration: '45s', tip: 'Keep your core engaged' },
            { instruction: 'March in place, lifting your knees high for 30 seconds.', duration: '30s', tip: 'Pump your arms as you march' },
            { instruction: 'Do 5 slow squats — lower down for 3 counts, rise for 3 counts.', duration: '60s', tip: 'Keep your weight in your heels' },
            { instruction: 'Stretch your arms overhead, reach to the left, then the right.', duration: '45s', tip: 'Breathe deeply with each stretch' },
            { instruction: 'Take 5 deep breaths — inhale for 4 counts, exhale for 6 counts.', duration: '60s', tip: 'Close your eyes and focus on your breathing' },
          ],
          meditation: [
            { instruction: 'Find a comfortable seated position and close your eyes.', duration: '30s', tip: 'Keep your spine straight but relaxed' },
            { instruction: 'Take 3 deep breaths. Inhale through your nose, exhale through your mouth.', duration: '30s' },
            { instruction: 'Focus on the sensation of breathing. Notice the air entering and leaving.', duration: '60s', tip: 'If your mind wanders, gently bring it back' },
            { instruction: 'Scan your body from head to toe. Notice any tension and release it.', duration: '90s', tip: 'Start at the top of your head and work down' },
            { instruction: 'Visualize a peaceful place — a beach, forest, or mountain. Immerse yourself.', duration: '60s' },
            { instruction: 'Slowly bring your awareness back. Wiggle your fingers and toes.', duration: '30s' },
            { instruction: 'Open your eyes gently. Take one final deep breath.', duration: '15s', tip: 'Carry this calm feeling with you' },
          ],
          social: [
            { instruction: 'Think of a colleague you haven\'t spoken to recently.', duration: '15s' },
            { instruction: 'Send them a quick message to check in or share something positive.', duration: '60s', tip: 'A simple "thinking of you" goes a long way' },
            { instruction: 'Reflect on one thing you appreciate about a team member.', duration: '30s' },
            { instruction: 'Share that appreciation with them — write it down or say it.', duration: '60s', tip: 'Be specific about what you appreciate' },
            { instruction: 'Plan one social activity for this week, even a virtual coffee.', duration: '45s' },
          ],
          learning: [
            { instruction: 'Choose a topic you\'ve been curious about.', duration: '15s' },
            { instruction: 'Spend 2 minutes reading or watching something about that topic.', duration: '120s', tip: 'Use the Learning Hub for curated content' },
            { instruction: 'Write down one key takeaway in your own words.', duration: '45s' },
            { instruction: 'Think about how you could apply this learning at work.', duration: '30s' },
            { instruction: 'Share your learning with a colleague or make a note to revisit.', duration: '30s', tip: 'Teaching others reinforces learning' },
          ],
          nutrition: [
            { instruction: 'Drink a full glass of water right now.', duration: '30s', tip: 'Room temperature water is easier to drink' },
            { instruction: 'Think about your next meal. Plan to include one extra vegetable.', duration: '30s' },
            { instruction: 'Take a mindful bite of a healthy snack. Chew slowly and notice flavors.', duration: '60s' },
            { instruction: 'Set a reminder to drink water in 2 hours.', duration: '15s' },
            { instruction: 'Reflect: How does your body feel when you eat well vs. when you don\'t?', duration: '45s' },
          ],
          sleep: [
            { instruction: 'Dim your screen brightness or enable night mode now.', duration: '15s' },
            { instruction: 'Take 5 slow, deep breaths to begin winding down.', duration: '45s' },
            { instruction: 'Write down 3 things that went well today.', duration: '60s', tip: 'Gratitude helps improve sleep quality' },
            { instruction: 'Set a "wind-down" alarm for 30 minutes before your target bedtime.', duration: '15s' },
            { instruction: 'Close your eyes. Tense and release each muscle group from toes to head.', duration: '90s', tip: 'Hold each tense for 5 seconds, then release' },
          ],
        };
        steps = fallbackSteps[rec.type] || fallbackSteps.meditation;
      }
      setActiveExercise({ title: rec.title, type: rec.type, steps });
    } catch (err) {
      toast.error('Could not generate exercise. Using guided template.');
      setActiveExercise({
        title: rec.title,
        type: rec.type,
        steps: [
          { instruction: 'Take a comfortable position and relax your shoulders.', duration: '30s' },
          { instruction: 'Close your eyes and take 3 deep breaths.', duration: '30s' },
          { instruction: 'Focus on the present moment. Let go of any worries.', duration: '60s' },
          { instruction: 'Set an intention for the rest of your day.', duration: '30s' },
          { instruction: 'Open your eyes and smile. You did great!', duration: '15s', tip: 'Carry this positive energy forward' },
        ],
      });
    } finally {
      setExerciseLoading(false);
    }
  };

  const startStepTimer = (durationStr: string) => {
    if (timerRef.current) clearInterval(timerRef.current);
    const match = durationStr.match(/(\d+)/);
    const seconds = match ? parseInt(match[1]) : 30;
    setExerciseTimer(seconds);
    setTimerActive(true);
    timerRef.current = setInterval(() => {
      setExerciseTimer(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          setTimerActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  return (
    <>
      <WellnessCheckInModal
        open={showWellnessModal && !user?.mustChangePassword}
        onClose={() => setShowWellnessModal(false)}
        onComplete={handleWellnessComplete}
      />

      <Zoom in={!open && !hideFab}>
        {/* No badge and no dot. The assistant is always there; it has nothing
            urgent to say, and a permanent alert on it only taught people to
            ignore alerts. The wellness check-in still opens on its own. */}
        <AiraFab onClick={() => setOpen(true)} hasPageFab={hasPageFab} aria-label={`Ask ${botName}`}>
          {botPictureUrl ? (
            <img src={botPictureUrl} alt={botName} style={{ width: isMobile ? 32 : 40, height: isMobile ? 32 : 40, borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <AiraIcon sx={{ fontSize: isMobile ? 28 : 34 }} />
          )}
        </AiraFab>
      </Zoom>

            {/* On a phone the assistant takes the whole screen: anchored at
                bottom 100 with 85vh height and 95vw + 24px width, the panel's
                header (and its close button) ran off the top and left edges. */}
            <Dialog
              open={open}
              onClose={() => setOpen(false)}
              maxWidth="md"
              fullScreen={isPhone && !minimized}
              PaperProps={{
                sx: isPhone
                  ? {
                      background: 'linear-gradient(180deg, var(--surface-subtle) 0%, var(--surface) 100%)',
                      overflow: 'hidden',
                      ...(minimized ? {
                        position: 'fixed', left: 12, right: 12, m: 0, width: 'auto', maxWidth: 'none',
                        bottom: 'calc(var(--app-bottom-inset) + 12px)', borderRadius: '20px',
                      } : {}),
                    }
                  : {
                      position: 'fixed',
                      bottom: 100,
                      right: 24,
                      m: 0,
                      width: minimized ? 320 : 480,
                      maxWidth: 'calc(100vw - 48px)',
                      height: minimized ? 'auto' : 650,
                      maxHeight: 'calc(100vh - 124px)',
                      borderRadius: '20px',
                      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                      background: 'linear-gradient(180deg, var(--surface-subtle) 0%, var(--surface) 100%)',
                      overflow: 'hidden',
                    }
              }}
              hideBackdrop={!isPhone}
              disableScrollLock={!isPhone}
            >
        <DialogTitle sx={{ 
          background: AIRA_GRADIENT,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={botPictureUrl || undefined}
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'rgba(255,255,255,0.2)',
                border: '2px solid rgba(255,255,255,0.3)',
              }}
            >
              {!botPictureUrl && <AiraIcon sx={{ fontSize: 20 }} />}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Ask {botName}</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
                AI Wellness Companion
              </Typography>
            </Box>
          </Box>
          <Box>
            <IconButton
              onClick={() => setShowBotCustomizer(true)}
              sx={{ color: 'white' }}
              size="small"
              title="Customize your AI bot"
            >
              <TuneIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => setMinimized(!minimized)} sx={{ color: 'white' }} size="small">
              <MinimizeIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => setOpen(false)} sx={{ color: 'white' }} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        {!minimized && (
          <DialogContent sx={{ p: 0 }}>
            <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" sx={{ minHeight: 56, '& .MuiTab-root': { minWidth: 0, px: 0.5, fontSize: '0.75rem', minHeight: 56 } }}>
              <Tab label="Chat" icon={<Chat sx={{ fontSize: 18 }} />} />
              <Tab label="Mood" icon={<Mood sx={{ fontSize: 18 }} />} />
              <Tab label="Goals" icon={<HealthAndSafety sx={{ fontSize: 18 }} />} />
              <Tab label="Tips" icon={<Lightbulb sx={{ fontSize: 18 }} />} />
            </Tabs>

                        {/* Chat Tab */}
                        {activeTab === 0 && (
                          <Box sx={{ height: 'calc(100% - 48px)', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                  {/* Suggested questions — shown until user sends their first message */}
                  {!mesairas.some(m => m.role === 'user') && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Suggested questions
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {SUGGESTED_QUESTIONS.map(q => (
                          <Chip
                            key={q}
                            label={q}
                            size="small"
                            onClick={() => handleSendSuggested(q)}
                            clickable
                            sx={{
                              bgcolor: 'white',
                              border: '1px solid #E2E8F0',
                              fontSize: '0.7rem',
                              '&:hover': { bgcolor: '#EFF6FF', borderColor: '#93C5FD' },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {mesairas.map((mesaira) => (
                    <Box key={mesaira.id} sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                      <MesairaBubble isUser={mesaira.role === 'user'}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.4, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{mesaira.content}</Typography>
                                <Typography variant="caption" sx={{ opacity: 0.7, mt: 0.5, display: 'block', fontSize: '0.65rem' }}>
                          {new Date(mesaira.timestamp).toLocaleTimeString()}
                        </Typography>
                        {/* Policy citations */}
                        {mesaira.citations && mesaira.citations.length > 0 && (
                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #E2E8F0' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem' }}>
                              Sources
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {mesaira.citations.map(c => (
                                <Chip
                                  key={c.id}
                                  icon={<PolicyIcon sx={{ fontSize: '11px !important' }} />}
                                  label={c.title}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </MesairaBubble>
                    </Box>
                  ))}
                  {isLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ mr: 1, bgcolor: 'primary.main' }}>
                        <AiraIcon />
                      </Avatar>
                      <GlassSpinner size={20} />
                      <Typography variant="body2" sx={{ ml: 1 }}>{botName} is thinking...</Typography>
                    </Box>
                  )}
                  <div ref={mesairasEndRef} />
                </Box>

                                <Box sx={{ p: 2, borderTop: '1px solid var(--hairline)', bgcolor: 'var(--surface)' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                                    <TextField
                                      fullWidth
                                      multiline
                                      maxRows={3}
                                      placeholder="Ask about policies, benefits, or anything else…"
                                      value={inputMesaira}
                                      onChange={(e) => setInputMesaira(e.target.value)}
                                      onKeyPress={handleKeyPress}
                                      variant="outlined"
                                      size="small"
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: '16px',
                                          bgcolor: 'var(--surface-subtle)',
                                          '&:hover': { bgcolor: 'var(--surface-muted)' },
                                          '&.Mui-focused': { bgcolor: 'var(--surface)' }
                                        }
                                      }}
                                    />
                                    <Tooltip title="Send message">
                                      <IconButton
                                        onClick={handleSendMesaira}
                                        disabled={!inputMesaira.trim() || isLoading}
                                        sx={{
                                          bgcolor: LIME_GREEN,
                                          color: 'var(--ink)',
                                          borderRadius: '12px',
                                          '&:hover': { bgcolor: LIME_GREEN_DARK },
                                          '&.Mui-disabled': { bgcolor: 'var(--color-border-main)', color: 'var(--ink-faint)' }
                                        }}
                                      >
                                        <SendIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
              </Box>
            )}

            {/* Mood Tracker Tab */}
            {activeTab === 1 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>Daily Mood Check-in</Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>How are you feeling?</Typography>
                        
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>Mood</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getMoodIcon(currentMood)}
                            <Slider
                              value={currentMood}
                              onChange={(e, value) => setCurrentMood(value as number)}
                              min={1}
                              max={5}
                              step={1}
                              marks
                              sx={{ flexGrow: 1 }}
                            />
                          </Box>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>Energy Level</Typography>
                          <Slider
                            value={currentEnergy}
                            onChange={(e, value) => setCurrentEnergy(value as number)}
                            min={1}
                            max={5}
                            step={1}
                            marks
                            color="secondary"
                          />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>Stress Level</Typography>
                          <Slider
                            value={currentStress}
                            onChange={(e, value) => setCurrentStress(value as number)}
                            min={1}
                            max={5}
                            step={1}
                            marks
                            color="warning"
                          />
                        </Box>

                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="Any notes about your day?"
                          value={moodNotes}
                          onChange={(e) => setMoodNotes(e.target.value)}
                          sx={{ mb: 2 }}
                        />

                        <Typography variant="body2" sx={{ mb: 1 }}>Activities today:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                          {activities.map((activity) => (
                            <Chip
                              key={activity}
                              label={activity}
                              onClick={() => {
                                setSelectedActivities(prev =>
                                  prev.includes(activity)
                                    ? prev.filter(a => a !== activity)
                                    : [...prev, activity]
                                );
                              }}
                              color={selectedActivities.includes(activity) ? 'primary' : 'default'}
                              variant={selectedActivities.includes(activity) ? 'filled' : 'outlined'}
                              size="small"
                            />
                          ))}
                        </Box>

                        <Button
                          variant="contained"
                          onClick={handleMoodSubmit}
                          fullWidth
                          startIcon={<CheckCircle />}
                        >
                          Log Mood
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>Recent Entries</Typography>
                        <List>
                          {moodHistory.slice(0, 5).map((entry) => (
                            <ListItem key={entry.id} divider>
                              <ListItemIcon>
                                {getMoodIcon(entry.mood)}
                              </ListItemIcon>
                              <ListItemText
                                primary={new Date(entry.date).toLocaleDateString()}
                                secondary={
                                  <Box>
                                    <Typography variant="caption" display="block">
                                      Mood: {entry.mood}/5 | Energy: {entry.energy}/5 | Stress: {entry.stress}/5
                                    </Typography>
                                    {entry.notes && (
                                      <Typography variant="caption" color="text.secondary">
                                        {entry.notes}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Wellness Goals Tab */}
            {activeTab === 2 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>Wellness Goals</Typography>
                
                <Grid container spacing={2}>
                  {wellnessGoals.map((goal) => (
                    <Grid item xs={12} md={6} key={goal.id}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{goal.title}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {goal.description}
                              </Typography>
                            </Box>
                            <Chip
                              label={goal.category}
                              size="small"
                              color={
                                goal.category === 'physical' ? 'success' :
                                goal.category === 'mental' ? 'primary' :
                                goal.category === 'social' ? 'secondary' : 'info'
                              }
                            />
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2">Progress</Typography>
                              <Typography variant="body2">
                                {goal.current}/{goal.target} {goal.unit}
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={getGoalProgress(goal)}
                              sx={{ height: 8, borderRadius: 4 }}
                              color={getGoalProgress(goal) >= 100 ? 'success' : 'primary'}
                            />
                          </Box>

                          <Typography variant="caption" color="text.secondary">
                            Deadline: {new Date(goal.deadline).toLocaleDateString()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Recommendations Tab */}
            {activeTab === 3 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>Personalized Recommendations</Typography>
                
                <Grid container spacing={2}>
                  {recommendations.map((rec) => (
                    <Grid item xs={12} md={6} key={rec.id}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                              {rec.icon}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{rec.title}</Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip label={rec.duration} size="small" />
                                <Chip 
                                  label={rec.difficulty} 
                                  size="small" 
                                  color={
                                    rec.difficulty === 'easy' ? 'success' :
                                    rec.difficulty === 'medium' ? 'warning' : 'error'
                                  }
                                />
                              </Box>
                            </Box>
                          </Box>

                          <Typography variant="caption" sx={{ mb: 1, display: 'block', color: 'text.secondary' }}>
                            {rec.description}
                          </Typography>

                          <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>Benefits:</Typography>
                          <List dense>
                            {rec.benefits.map((benefit, index) => (
                              <ListItem key={index} sx={{ py: 0 }}>
                                <ListItemIcon sx={{ minWidth: 20 }}>
                                  <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={benefit} 
                                  primaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            ))}
                          </List>

                          <Button
                            variant="contained"
                            fullWidth
                            size="small"
                            sx={{ mt: 1, bgcolor: LIME_GREEN, color: 'var(--ink)', fontWeight: 600, '&:hover': { bgcolor: LIME_GREEN_DARK } }}
                            startIcon={exerciseLoading ? <GlassSpinner size={14} /> : <PlayArrow />}
                            disabled={exerciseLoading}
                            onClick={() => handleStartActivity(rec)}
                          >
                            {exerciseLoading ? 'Generating...' : 'Start Guided Exercise'}
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {activeExercise && (
                  <Card sx={{ mt: 2, border: '2px solid', borderColor: LIME_GREEN, borderRadius: 3 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton size="small" onClick={() => { setActiveExercise(null); setTimerActive(false); if (timerRef.current) clearInterval(timerRef.current); }}>
                            <ArrowBack fontSize="small" />
                          </IconButton>
                          <Typography variant="subtitle2" fontWeight={700}>{activeExercise.title}</Typography>
                        </Box>
                        <Chip label={`Step ${exerciseStep + 1}/${activeExercise.steps.length}`} size="small" color="primary" />
                      </Box>

                      <LinearProgress variant="determinate" value={((exerciseStep + 1) / activeExercise.steps.length) * 100} sx={{ mb: 2, height: 6, borderRadius: 3, bgcolor: 'var(--color-border-main)', '& .MuiLinearProgress-bar': { bgcolor: LIME_GREEN } }} />

                      <Paper sx={{ p: 2, bgcolor: 'var(--surface-subtle)', borderRadius: 2, mb: 1.5, minHeight: 80 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.85rem' }}>
                          {activeExercise.steps[exerciseStep]?.instruction}
                        </Typography>
                        {activeExercise.steps[exerciseStep]?.tip && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            Tip: {activeExercise.steps[exerciseStep].tip}
                          </Typography>
                        )}
                      </Paper>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Timer sx={{ fontSize: 16, color: timerActive ? 'primary.main' : 'text.secondary' }} />
                          <Typography variant="caption" sx={{ fontWeight: timerActive ? 700 : 400, color: timerActive ? 'primary.main' : 'text.secondary' }}>
                            {timerActive ? `${exerciseTimer}s remaining` : activeExercise.steps[exerciseStep]?.duration}
                          </Typography>
                          {!timerActive && (
                            <Button size="small" variant="text" sx={{ minWidth: 'auto', fontSize: '0.65rem', p: '2px 6px' }} onClick={() => startStepTimer(activeExercise.steps[exerciseStep]?.duration || '30s')}>
                              Start Timer
                            </Button>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" variant="outlined" disabled={exerciseStep === 0} onClick={() => { setExerciseStep(s => s - 1); setTimerActive(false); if (timerRef.current) clearInterval(timerRef.current); }}>
                            Back
                          </Button>
                          {exerciseStep < activeExercise.steps.length - 1 ? (
                            <Button size="small" variant="contained" endIcon={<NavigateNext />} sx={{ bgcolor: LIME_GREEN, color: 'var(--ink)', '&:hover': { bgcolor: LIME_GREEN_DARK } }} onClick={() => { setExerciseStep(s => s + 1); setTimerActive(false); if (timerRef.current) clearInterval(timerRef.current); }}>
                              Next
                            </Button>
                          ) : (
                            <Button size="small" variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => { setActiveExercise(null); setTimerActive(false); if (timerRef.current) clearInterval(timerRef.current); toast.success('Exercise completed! Great job!'); }}>
                              Done
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
          </DialogContent>
        )}

        {minimized && (
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body1">
                {botName} is here when you need support
              </Typography>
              <Button
                variant="contained"
                onClick={() => setMinimized(false)}
                sx={{ mt: 2 }}
              >
                Open Full Interface
              </Button>
            </Box>
          </DialogContent>
        )}
      </Dialog>

      <UserBotCustomizer
        open={showBotCustomizer}
        onClose={() => setShowBotCustomizer(false)}
      />
    </>
  );
}

export default EnhancedAiraAI;
